import { useState } from "react";
import { useCreateInputType } from "../../hooks/useInputTypes";
import { toast } from "sonner";
import { useConfirm } from "@/shared/contexts/confirm-context";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Field, FieldLabel } from "@/components/ui/field";
import { Plus } from "lucide-react";

const initialFormData = {
  code: "",
  name: "",
  description: "",
};

export const CreateInputTypeDialog = () => {
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState(initialFormData);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string[]>>({});

  const createMutation = useCreateInputType();
  const confirm = useConfirm();

  const handleChange = (
    field: keyof typeof initialFormData,
    value: string
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (fieldErrors[field]) {
      setFieldErrors((prev) => {
        const { [field]: _, ...rest } = prev;
        return rest;
      });
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setFieldErrors({});

    confirm({
      title: "Create input type?",
      description: `“${formData.name}” will be added as a new input type.`,
      confirmLabel: "Create",
      onConfirm: () =>
        createMutation
          .mutateAsync(formData)
          .then(() => {
            toast.success("Input type created successfully");
            setFormData(initialFormData);
            setOpen(false);
          })
          .catch((error: any) => {
            const errorData = error.response?.data;
            const errorsList = errorData?.errors || [];

            if (Array.isArray(errorsList) && errorsList.length > 0) {
              // Parse field-specific errors from the API response
              const parsedErrors: Record<string, string[]> = {};

              errorsList.forEach((errorMsg: string) => {
                const [fieldName, ...messageParts] = errorMsg.split(':');
                const message = messageParts.join(':').trim();

                // Map API field names to form field names
                const fieldMapping: Record<string, string> = {
                  'Code': 'code',
                  'Name': 'name',
                  'Description': 'description',
                };

                const mappedFieldName = fieldMapping[fieldName.trim()] || fieldName.trim().toLowerCase();

                if (!parsedErrors[mappedFieldName]) {
                  parsedErrors[mappedFieldName] = [];
                }
                parsedErrors[mappedFieldName].push(message);

                // Show individual toast for each error
                toast.error(`${fieldName.trim()}: ${message}`);
              });

              setFieldErrors(parsedErrors);
            } else if (errorData?.message) {
              toast.error(errorData.message);
            } else {
              toast.error(error.message || "Failed to create input type");
            }
            throw error;
          }),
    });
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="gap-2">
          <Plus className="w-4 h-4" />
          New Input Type
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Create New Input Type</DialogTitle>
          <DialogDescription>
            Add a new input type to the system
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">

          <Field>
            <FieldLabel htmlFor="code">
              Code <span className="text-red-500">*</span>
            </FieldLabel>
            <Input
              id="code"
              placeholder="e.g., SEED, FERT"
              value={formData.code}
              onChange={(e) => handleChange("code", e.target.value)}
              required
              disabled={createMutation.isPending}
            />
            {fieldErrors.code && (
              <div className="text-red-500 text-xs mt-1">
                {fieldErrors.code.join(", ")}
              </div>
            )}
          </Field>

          <Field>
            <FieldLabel htmlFor="name">
              Name <span className="text-red-500">*</span>
            </FieldLabel>
            <Input
              id="name"
              placeholder="e.g., Seeds, Fertilizer"
              value={formData.name}
              onChange={(e) => handleChange("name", e.target.value)}
              required
              disabled={createMutation.isPending}
            />
            {fieldErrors.name && (
              <div className="text-red-500 text-xs mt-1">
                {fieldErrors.name.join(", ")}
              </div>
            )}
          </Field>

          <Field>
            <FieldLabel htmlFor="description">Description</FieldLabel>
            <textarea
              id="description"
              placeholder="Brief description of this input type"
              value={formData.description}
              onChange={(e) => handleChange("description", e.target.value)}
              disabled={createMutation.isPending}
              className="flex min-h-20 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
            />
            {fieldErrors.description && (
              <div className="text-red-500 text-xs mt-1">
                {fieldErrors.description.join(", ")}
              </div>
            )}
          </Field>

          <Button
            type="submit"
            className="w-full"
            disabled={createMutation.isPending}
          >
            {createMutation.isPending ? "Creating..." : "Create Input Type"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};